import { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface ViewerPresenceOptions {
  streamId: string | null;
  matchId?: string;
}

export function useViewerPresence({ streamId, matchId }: ViewerPresenceOptions) {
  const { user } = useAuth();
  const [viewerCount, setViewerCount] = useState(0);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const peakRef = useRef(0);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateServerStats = useCallback(async (currentCount: number) => {
    if (!streamId) return;

    if (currentCount > peakRef.current) {
      peakRef.current = currentCount;
      await supabase
        .from('match_streams')
        .update({ peak_viewers: currentCount })
        .eq('id', streamId);
    }
  }, [streamId]);

  const debouncedUpdate = useCallback((count: number) => {
    if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
    updateTimeoutRef.current = setTimeout(() => updateServerStats(count), 3000);
  }, [updateServerStats]);

  useEffect(() => {
    if (!streamId) return;

    const channelName = `viewers:${streamId}`;
    const channel = supabase.channel(channelName, {
      config: { presence: { key: user?.id || `anon-${Math.random().toString(36).slice(2)}` } },
    });

    let viewCounted = false;

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const count = Object.keys(state).length;
        setViewerCount(count);
        debouncedUpdate(count);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            user_id: user?.id || null,
            display_name: user?.user_metadata?.full_name || 'Viewer',
            joined_at: new Date().toISOString(),
          });

          if (!viewCounted) {
            viewCounted = true;
            const { data } = await supabase
              .from('match_streams')
              .select('total_views')
              .eq('id', streamId)
              .single();
            if (data) {
              await supabase
                .from('match_streams')
                .update({ total_views: (data.total_views || 0) + 1 })
                .eq('id', streamId);
            }
          }
        }
      });

    channelRef.current = channel;

    return () => {
      if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
      channel.untrack();
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [streamId, user?.id, debouncedUpdate]);

  return { viewerCount };
}

export function useLiveStreamCount() {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCount = async () => {
      const { count: liveCount, error } = await supabase
        .from('match_streams')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'live');

      if (!error && liveCount !== null) {
        setCount(liveCount);
      }
      setLoading(false);
    };

    fetchCount();

    const channel = supabase
      .channel('live_stream_count')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'match_streams' },
        () => { fetchCount(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  return { count, loading };
}
